import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Typography,
  Input,
  Modal,
  Button,
  Popover,
  Layout,
  Skeleton,
  Table,
  Select,
  Pagination,
  Tag,
  Space,
  message,
} from "antd";
import dayjs from "dayjs";
import { FaSearch, FaFilter, FaCaretDown, FaEye } from "react-icons/fa";
import { Get } from "../../config/api/get";
import { Patch } from "../../config/api/patch";
import { ASSOCIATE_ADMIN } from "../../config/constants";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const STATUS_COLORS = {
  PENDING: "gold",
  ACTIVE: "green",
  INACTIVE: "red",
};

function AssociateManagement() {
  const token = useSelector((state) => state.user.userToken);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [associates, setAssociates] = useState([]);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: 10,
    totalDocs: 0,
    totalPages: 0,
  });

  const [filter, setFilter] = useState({
    status: null,
    keyword: "",
  });

  const startIndex =
    (paginationConfig.pageNumber - 1) * paginationConfig.limit + 1;
  const endIndex = Math.min(
    startIndex + paginationConfig.limit - 1,
    paginationConfig.totalDocs
  );
  const recordMessage = `Showing records ${endIndex} of ${paginationConfig.totalDocs}`;

  useEffect(() => {
    getAssociates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAssociates = async (pageNumber, pageSize, keyword, reset = false) => {
    setLoading(true);
    try {
      const query = {
        page: pageNumber
          ? String(pageNumber)
          : String(paginationConfig.pageNumber),
        limit: pageSize
          ? String(pageSize)
          : String(paginationConfig.limit),
      };
      if (!reset && filter.status) query.status = filter.status;
      const search = keyword !== undefined ? keyword : filter.keyword;
      if (!reset && search) query.keyword = search;

      const response = await Get(ASSOCIATE_ADMIN.list, token, query);
      setLoading(false);
      if (response?.status) {
        setAssociates(response?.data?.associates || []);
        const pageInfo = response?.data?.pageInfo || {};
        setPaginationConfig({
          pageNumber: pageInfo.page || 1,
          limit: pageInfo.limit || 10,
          totalDocs: pageInfo.totalDocs || 0,
          totalPages: pageInfo.totalPages || 0,
        });
      } else {
        message.error(response?.message || "Failed to load associates");
      }
    } catch (error) {
      setLoading(false);
      message.error(error?.message || "Failed to load associates");
    }
  };

  const handlePageChange = (pageNumber) => {
    setPaginationConfig((prev) => ({ ...prev, pageNumber }));
    getAssociates(pageNumber);
  };

  const handleLimitChange = (pageSize) => {
    setPaginationConfig((prev) => ({ ...prev, limit: pageSize, pageNumber: 1 }));
    getAssociates(1, pageSize);
  };

  const resetFilter = () => {
    setFilter({ status: null, keyword: "" });
    getAssociates(1, paginationConfig.limit, "", true);
  };

  const openConfirm = (associate, action) => {
    setSelectedAssociate(associate);
    setConfirmAction(action);
    setModalOpen(true);
  };

  const runConfirmedAction = async () => {
    if (!selectedAssociate || !confirmAction) return;
    setActionLoading(true);
    try {
      let path = "";
      if (confirmAction === "approve") {
        path = `${ASSOCIATE_ADMIN.approve}${selectedAssociate._id}/approve`;
      } else if (confirmAction === "decline") {
        path = `${ASSOCIATE_ADMIN.decline}${selectedAssociate._id}/decline`;
      } else if (confirmAction === "toggle") {
        path = `${ASSOCIATE_ADMIN.toggle}${selectedAssociate._id}/toggle`;
      }

      const response = await Patch(path, token, {});
      if (response?.status) {
        message.success(response?.message || "Updated successfully");
        setModalOpen(false);
        setSelectedAssociate(null);
        setConfirmAction(null);
        getAssociates(paginationConfig.pageNumber);
      } else {
        message.error(response?.message || "Action failed");
      }
    } catch (error) {
      message.error(error?.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const coachName = (coach) => {
    if (!coach) return "—";
    if (typeof coach === "object") {
      return `${coach.firstName || ""} ${coach.lastName || ""}`.trim() || "—";
    }
    return "—";
  };

  const confirmTitle = () => {
    if (confirmAction === "approve") return "Approve associate";
    if (confirmAction === "decline") return "Decline associate";
    if (confirmAction === "toggle") {
      return selectedAssociate?.status === "ACTIVE"
        ? "Deactivate associate"
        : "Activate associate";
    }
    return "Confirm";
  };

  const confirmText = () => {
    const name = selectedAssociate
      ? `${selectedAssociate.firstName} ${selectedAssociate.lastName}`
      : "this associate";
    if (confirmAction === "approve") {
      return `Approve ${name}? Login credentials will be emailed automatically.`;
    }
    if (confirmAction === "decline") {
      return `Decline ${name}? They will not be able to sign in.`;
    }
    if (confirmAction === "toggle") {
      return selectedAssociate?.status === "ACTIVE"
        ? `Deactivate ${name}?`
        : `Activate ${name}?`;
    }
    return "Are you sure?";
  };

  const columns = [
    {
      title: "S. No.",
      key: "index",
      width: 80,
      render: (_, __, index) => (index < 9 ? "0" : "") + (index + 1),
    },
    {
      title: "Associate Name",
      key: "name",
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
    },
    {
      title: "Trainer",
      key: "coach",
      render: (_, record) => coachName(record.coach),
    },
    {
      title: "Registered",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? dayjs(date).format("MM/DD/YYYY") : "—"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record, index) =>
        status === "PENDING" ? (
          <Tag color={STATUS_COLORS[status]}>{status}</Tag>
        ) : (
          <Select
            className={status === "ACTIVE" ? "greenSelect" : "redSelect"}
            suffixIcon={<FaCaretDown style={{ fontSize: "16px" }} />}
            value={status[0] + status.slice(1).toLowerCase()}
            bordered={false}
            onChange={() => openConfirm(associates[index], "toggle")}
            options={[
              { value: "ACTIVE", label: "Active" },
              { value: "INACTIVE", label: "Inactive" },
            ]}
          />
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      render: (_, record) => (
        <Space size="small">
          <FaEye
            style={{ fontSize: 16, color: "#2484c6", cursor: "pointer" }}
            title="View details"
            onClick={() => navigate(`/associate-management/${record._id}`)}
          />
          {record.status === "PENDING" ? (
            <>
              <Button
                className="loginButton"
                size="small"
                style={{
                  minWidth: 88,
                  height: 32,
                  padding: "0 14px",
                  fontSize: 13,
                }}
                onClick={() => openConfirm(record, "approve")}
              >
                Approve
              </Button>
              <Button
                className="loginButton2"
                size="small"
                style={{
                  minWidth: 88,
                  height: 32,
                  padding: "0 14px",
                  fontSize: 13,
                }}
                onClick={() => openConfirm(record, "decline")}
              >
                Decline
              </Button>
            </>
          ) : null}
        </Space>
      ),
    },
  ];

  const filterContent = (
    <div className="filterDropdown">
      <div>
        <p className="mainLabel" style={{ padding: "10px" }}>
          Filter
        </p>
      </div>
      <hr style={{ margin: 0 }} />
      <div className="filterDropdownBody">
        <p className="mainLabel">Filter by Status:</p>
        <Select
          size="large"
          className="filterSelectBox"
          placeholder="Select Status"
          value={filter.status}
          onChange={(value) => setFilter((f) => ({ ...f, status: value }))}
          allowClear
          style={{ width: "100%", marginBottom: 10, textAlign: "left" }}
          options={[
            { value: "PENDING", label: "Pending" },
            { value: "ACTIVE", label: "Active" },
            { value: "INACTIVE", label: "Inactive" },
          ]}
        />
        <Button
          type="primary"
          shape="round"
          block
          size="large"
          style={{ marginBottom: 10 }}
          className="mainButton primaryButton"
          onClick={() => getAssociates(1)}
        >
          Apply
        </Button>
        <Button
          type="primary"
          shape="round"
          block
          size="large"
          className="mainButton primaryButton2"
          onClick={resetFilter}
        >
          Clear All
        </Button>
      </div>
    </div>
  );

  return (
    <Layout className="configuration">
      <div className="boxDetails2">
        <Row style={{ padding: "10px 20px 0" }}>
          <h1 className="pageTitle">Associate Management</h1>
        </Row>

        <Row style={{ padding: "10px 20px" }}>
          <Col xs={24} md={12}>
            <h5 style={{ display: "inline", fontSize: 16 }}>Show : </h5>
            <Select
              size="large"
              className="chartSelectBox"
              value={paginationConfig.limit}
              onChange={handleLimitChange}
              style={{ width: 70, textAlign: "left" }}
              options={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
              ]}
            />
            &emsp;
            <h5 style={{ display: "inline", fontSize: 16 }}>Entries</h5>
          </Col>
          <Col
            xs={24}
            md={12}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Popover
              content={filterContent}
              trigger="click"
              open={open}
              onOpenChange={setOpen}
              placement="bottomRight"
              arrow={false}
            >
              <Button
                style={{
                  padding: "8px 11px",
                  height: "auto",
                  borderRadius: "50px",
                  backgroundColor: "#00a79d",
                  border: "none",
                }}
              >
                <FaFilter style={{ fontSize: 16, color: "white" }} />
              </Button>
            </Popover>
            &emsp;
            <Input
              style={{ width: 280 }}
              className="mainInput dashInput"
              placeholder="Search by name, email, position..."
              value={filter.keyword}
              onChange={(e) =>
                setFilter((f) => ({ ...f, keyword: e.target.value }))
              }
              onPressEnter={() => getAssociates(1)}
              suffix={
                <FaSearch
                  style={{
                    color: "#2484c6",
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                  onClick={() => getAssociates(1)}
                />
              }
            />
          </Col>
        </Row>

        <Row style={{ padding: "10px 20px" }}>
          {loading ? (
            <div style={{ width: "100%", textAlign: "center" }}>
              <Skeleton active />
            </div>
          ) : (
            <Table
              className="styledTable"
              dataSource={associates}
              columns={columns}
              rowKey="_id"
              pagination={false}
            />
          )}
        </Row>

        <Row style={{ padding: "10px 20px" }}>
          <Col xs={24} md={12}>
            <p>{recordMessage}</p>
          </Col>
          <Col
            xs={24}
            md={12}
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Pagination
              className="styledPagination"
              onChange={handlePageChange}
              current={paginationConfig.pageNumber}
              pageSize={paginationConfig.limit}
              total={paginationConfig.totalDocs}
            />
          </Col>
        </Row>
      </div>

      <Modal
        open={modalOpen}
        onOk={runConfirmedAction}
        onCancel={() => {
          setModalOpen(false);
          setConfirmAction(null);
          setSelectedAssociate(null);
        }}
        okText="Yes"
        cancelText="No"
        confirmLoading={actionLoading}
        className="StyledModal"
        title={confirmTitle()}
        cancelButtonProps={{
          style: {
            border: "2px solid #2484c6",
            color: "#2484c6",
            height: "auto",
            padding: "6px 35px",
            borderRadius: "50px",
            fontSize: "16px",
          },
        }}
        okButtonProps={{
          style: {
            backgroundColor: "#2484c6",
            color: "white",
            height: "auto",
            padding: "5px 35px",
            borderRadius: "50px",
            fontSize: "16px",
            border: "2px solid #2484c6",
          },
        }}
      >
        <Typography.Text>{confirmText()}</Typography.Text>
      </Modal>
    </Layout>
  );
}

export default AssociateManagement;
